import { useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import {
  logout,
  passkeyRegisterStart,
  passkeyRegisterFinish,
  AuthError,
} from '../lib/authClient';
import TopBar from '../components/TopBar';
import { GUIDE_SECTIONS } from './help/guideContent';
import GuideSection from './help/GuideSection';
import styles from './HelpGuide.module.css';

interface HelpGuideProps {
  onBack: () => void;
  onLoggedOut: () => void;
}

// Log out asks first (H5): 'confirming' shows "Yes, log out" / "No, stay signed in".
type LogoutStep = 'idle' | 'confirming' | 'logging-out' | 'error';

type SetupStep =
  | { name: 'idle' }
  | { name: 'pending' }
  | { name: 'done'; message: string; role: 'status' | 'alert' };

// A 401 never logs the user out (AD-06) — it shows a retryable message. Only the
// session guard's 401 ("Unauthorized") means the session is gone; a failed
// verification is also a 401 and gets the generic message.
function setupOutcome(err: unknown): { message: string; role: 'status' | 'alert' } {
  if (err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
    return { message: 'Setup was cancelled.', role: 'status' };
  }
  // excludeCredentials fired: this authenticator already holds a passkey for the user
  if (err instanceof Error && err.name === 'InvalidStateError') {
    return { message: 'Sign-in is already set up on this device.', role: 'status' };
  }
  if (err instanceof AuthError && err.status === 401 && err.message === 'Unauthorized') {
    return { message: "Your session couldn't be confirmed. Please try again.", role: 'alert' };
  }
  return { message: 'Could not set up sign-in. Please try again.', role: 'alert' };
}

// The Guide (H1): a landing screen of section buttons, each section on its own screen.
// Set up and Log out sit at the bottom of the landing screen (H5).
export default function HelpGuide({ onBack, onLoggedOut }: HelpGuideProps) {
  const [step, setStep] = useState<LogoutStep>('idle');
  const [setup, setSetup] = useState<SetupStep>({ name: 'idle' });
  const [openSection, setOpenSection] = useState<number | null>(null);

  // Each screen opens at its top.
  function showSection(index: number | null) {
    setOpenSection(index);
    window.scrollTo(0, 0);
  }

  async function handleLogout() {
    setStep('logging-out');
    try {
      await logout();
      onLoggedOut();
    } catch {
      setStep('error');
    }
  }

  // Keep the tap → startRegistration sequence to one request: WebAuthn must run
  // inside the tap's user activation on iPhone.
  async function handleSetUpPasskey() {
    setSetup({ name: 'pending' });
    try {
      const options = await passkeyRegisterStart();
      const response = await startRegistration({ optionsJSON: options });
      await passkeyRegisterFinish(response);
      setSetup({
        name: 'done',
        message: 'Sign-in is set up on this device.',
        role: 'status',
      });
    } catch (err) {
      setSetup({ name: 'done', ...setupOutcome(err) });
    }
  }

  if (openSection !== null) {
    const next = openSection + 1;
    return (
      <GuideSection
        section={GUIDE_SECTIONS[openSection]}
        onBack={() => showSection(null)}
        onNext={next < GUIDE_SECTIONS.length ? () => showSection(next) : undefined}
      />
    );
  }

  const isLoggingOut = step === 'logging-out';
  const isSettingUp = setup.name === 'pending';
  // One action at a time. The TopBar's Home stays enabled: TopBar takes no disabled state (Q3a).
  const isBusy = isLoggingOut || isSettingUp;

  return (
    <main className={styles.container}>
      <TopBar title="Help / Guide" backLabel="Home" onBack={onBack} />

      <div className={styles.sectionList}>
        {GUIDE_SECTIONS.map((section, i) => (
          <button
            key={section.title}
            type="button"
            className={styles.sectionButton}
            onClick={() => showSection(i)}
            disabled={isBusy}
          >
            {section.title}
          </button>
        ))}
      </div>

      <div className={styles.sectionList}>
        <button
          className={styles.secondaryButton}
          onClick={handleSetUpPasskey}
          disabled={isBusy}
        >
          {isSettingUp ? 'Setting up…' : 'Set up sign-in on this device'}
        </button>

        {setup.name === 'done' && (
          <p
            className={setup.role === 'alert' ? styles.errorMessage : styles.statusMessage}
            role={setup.role}
          >
            {setup.message}
          </p>
        )}

        {step === 'confirming' || isLoggingOut ? (
          <>
            <p className={styles.confirmText}>Log out on this device? You will need to sign in again.</p>
            <button className={styles.primaryButton} onClick={handleLogout} disabled={isBusy}>
              {isLoggingOut ? 'Logging out…' : 'Yes, log out'}
            </button>
            <button
              className={styles.secondaryButton}
              onClick={() => setStep('idle')}
              disabled={isBusy}
            >
              No, stay signed in
            </button>
          </>
        ) : (
          <button
            className={styles.primaryButton}
            onClick={() => setStep('confirming')}
            disabled={isBusy}
          >
            Log out
          </button>
        )}

        {step === 'error' && (
          <p className={styles.errorMessage} role="alert">
            Could not log out. Please try again.
          </p>
        )}
      </div>
    </main>
  );
}
